/**
 * Pulse (docs/CONTRACT-pulse.md sections 1, 6.3, 9, 11, 12 and 14): the shapes the admin reads from
 * `/v1/admin/pulse/*`, the closed vocabularies with the words people see, the list query, sort and export helpers, the
 * Settings form (the service's `pulseSettingsSchema` edited in plain words, a save sending only what changed), and the
 * health rules as sentences with the current numbers. Pure: no React, no fetch (lib/api.ts makes the calls).
 *
 * Nothing here names a person: Pulse records carry no user, device or conversation, and the screens say so.
 */
import { errorText } from './errors';

// ------------------------------------------------------------------------------------------------ vocabularies (section 3)

export type PulseDomain = 'gyro-ai-for-revit' | 'bluebeam-revu' | 'ppp' | 'm365' | 'documents' | 'starscribe' | 'knowledge' | 'connected-apps' | 'desktop' | 'help' | 'chat';
export type PulseOutcome = 'done' | 'done-with-gaps' | 'answered' | 'proposed-not-confirmed' | 'could-not' | 'unknown';
export type PulseOutcomeReason = 'no_tool' | 'not_connected' | 'no_permission' | 'refused' | 'unavailable' | 'timeout' | 'failed' | 'too_large' | 'rate_limited' | 'needs_update' | 'connector_contract' | 'not_found' | 'other';
export type PulseHealthState = 'healthy' | 'degraded' | 'failing' | 'quarantined' | 'recovering';
export type PulseRating = 'worked' | 'partly' | 'failed';
export type PulseProblemStatus = 'new' | 'investigating' | 'fix-proposed' | 'fixed-in' | 'verified' | 'wont-fix' | 'duplicate-of';
/** The one lock mode (the owner, D5: warn only, no auto switch-off; the quarantine modes were removed 2026-10-23). */
export type PulseLockMode = 'warn';
export type PulseSubjectKind = 'tool' | 'mcp' | 'mcp-server' | 'receiver' | 'connector' | 'ppp-source';

/** The tone and word a state reads with, the same on every page (ui/StatusChip's ChipSpec shape). */
export interface PulseChip { tone: 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'accent'; label: string }

/** Every domain, Gyro AI for Revit first (the owner's order for the scorecards). */
export const PULSE_DOMAINS: readonly PulseDomain[] = ['gyro-ai-for-revit', 'bluebeam-revu', 'ppp', 'm365', 'documents', 'starscribe', 'knowledge', 'connected-apps', 'desktop', 'help', 'chat'];

export const DOMAIN_LABELS: Record<PulseDomain, string> = {
  'gyro-ai-for-revit': 'Gyro AI for Revit',
  'bluebeam-revu': 'Bluebeam Revu',
  ppp: 'PPP',
  m365: 'Microsoft 365',
  documents: 'Documents',
  starscribe: 'StarScribe',
  knowledge: 'Company knowledge',
  'connected-apps': 'Connected apps',
  desktop: 'Desktop',
  help: 'Help',
  chat: 'Chat',
};

export function domainLabel(domain: string | null | undefined): string {
  return domain && domain in DOMAIN_LABELS ? DOMAIN_LABELS[domain as PulseDomain] : domain || '—';
}

export const PULSE_OUTCOMES: readonly PulseOutcome[] = ['done', 'done-with-gaps', 'answered', 'proposed-not-confirmed', 'could-not', 'unknown'];

export const OUTCOME_CHIPS: Record<PulseOutcome, PulseChip> = {
  done: { tone: 'ok', label: 'done' },
  'done-with-gaps': { tone: 'warn', label: 'done with gaps' },
  answered: { tone: 'ok', label: 'answered' },
  'proposed-not-confirmed': { tone: 'neutral', label: 'proposed, not confirmed' },
  'could-not': { tone: 'bad', label: 'could not' },
  unknown: { tone: 'neutral', label: 'unknown' },
};

export function outcomeChip(outcome: string | null | undefined): PulseChip {
  return outcome && outcome in OUTCOME_CHIPS ? OUTCOME_CHIPS[outcome as PulseOutcome] : { tone: 'neutral', label: outcome || 'pending' };
}

/** Reasons as the mockups spell them: lower case, spaces for underscores. */
export const REASON_LABELS: Record<PulseOutcomeReason, string> = {
  no_tool: 'no tool', not_connected: 'not connected', no_permission: 'no permission', refused: 'refused', unavailable: 'unavailable',
  timeout: 'timeout', failed: 'failed', too_large: 'too large', rate_limited: 'rate limited', needs_update: 'needs update',
  connector_contract: 'connector contract', not_found: 'not found', other: 'other',
};

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return '—';
  return reason in REASON_LABELS ? REASON_LABELS[reason as PulseOutcomeReason] : reason.replace(/_/g, ' ');
}

export const HEALTH_CHIPS: Record<PulseHealthState, PulseChip> = {
  healthy: { tone: 'ok', label: 'healthy' },
  degraded: { tone: 'warn', label: 'degraded' },
  failing: { tone: 'bad', label: 'failing' },
  quarantined: { tone: 'bad', label: 'quarantined' },
  recovering: { tone: 'info', label: 'recovering' },
};

export function healthChip(state: string | null | undefined): PulseChip {
  return state && state in HEALTH_CHIPS ? HEALTH_CHIPS[state as PulseHealthState] : { tone: 'neutral', label: state || 'unknown' };
}

export const RATING_CHIPS: Record<PulseRating, PulseChip> = {
  worked: { tone: 'ok', label: 'Worked' },
  partly: { tone: 'warn', label: 'Partly' },
  failed: { tone: 'bad', label: "Didn't work" },
};

/** The chip for a feedback entry: the card's rating, or the thumb. */
export function feedbackChip(entry: Pick<PulseFeedbackRow, 'kind' | 'rating' | 'thumb'>): PulseChip {
  if (entry.kind === 'card' && entry.rating) return RATING_CHIPS[entry.rating];
  if (entry.thumb === 'up') return { tone: 'ok', label: 'Thumbs up' };
  if (entry.thumb === 'down') return { tone: 'bad', label: 'Thumbs down' };
  return { tone: 'neutral', label: 'No rating' };
}

export const PROBLEM_STATUS_CHIPS: Record<PulseProblemStatus, PulseChip> = {
  new: { tone: 'bad', label: 'new' },
  investigating: { tone: 'warn', label: 'investigating' },
  'fix-proposed': { tone: 'info', label: 'fix proposed' },
  'fixed-in': { tone: 'info', label: 'fixed in' },
  verified: { tone: 'ok', label: 'verified' },
  'wont-fix': { tone: 'neutral', label: "won't fix" },
  'duplicate-of': { tone: 'neutral', label: 'duplicate' },
};

export function problemStatusChip(problem: Pick<PulseProblem, 'status' | 'fixedInVersion' | 'duplicateOf'>): PulseChip {
  const spec = PROBLEM_STATUS_CHIPS[problem.status] ?? { tone: 'neutral', label: problem.status };
  if (problem.status === 'fixed-in' && problem.fixedInVersion) return { ...spec, label: `fixed in ${problem.fixedInVersion}` };
  if (problem.status === 'duplicate-of' && problem.duplicateOf) return { ...spec, label: `duplicate of ${problem.duplicateOf}` };
  return spec;
}

/** The statuses the Problems list calls "open" (its default filter). */
export const OPEN_PROBLEM_STATUSES: readonly PulseProblemStatus[] = ['new', 'investigating', 'fix-proposed', 'fixed-in'];

/** A surface as people read it: `office:word` is the Word pane, `web:orbit` is Gyro on the web in Orbit. */
export function surfaceLabel(surface: string | null | undefined): string {
  if (!surface) return '—';
  if (surface === 'desktop') return 'desktop';
  if (surface === 'teams') return 'Teams';
  if (surface === 'hub') return 'the Hub';
  if (surface.startsWith('office:')) {
    const host = surface.slice('office:'.length);
    const names: Record<string, string> = { word: 'Word pane', outlook: 'Outlook pane', excel: 'Excel pane', powerpoint: 'PowerPoint pane' };
    return names[host] ?? `${host} pane`;
  }
  if (surface.startsWith('web:')) return `web · ${surface.slice('web:'.length)}`;
  if (surface === 'web') return 'web';
  return surface;
}

/** The surface filter's choices (the `web:<appKey>` family is one choice, `web`, which the service reads as a prefix). */
export const SURFACE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'desktop', label: 'Desktop' },
  { value: 'web', label: 'Gyro on the web' },
  { value: 'office:word', label: 'Word pane' },
  { value: 'office:outlook', label: 'Outlook pane' },
  { value: 'office:excel', label: 'Excel pane' },
  { value: 'office:powerpoint', label: 'PowerPoint pane' },
  { value: 'teams', label: 'Teams' },
  { value: 'hub', label: 'The Hub' },
];

export const SUBJECT_KIND_LABELS: Record<PulseSubjectKind, string> = {
  tool: 'tool', mcp: 'MCP tool', 'mcp-server': 'MCP server', receiver: 'connected app', connector: 'connector', 'ppp-source': 'PPP source',
};

/** `mcp:revit/join_walls` → `join_walls`, with the server as its small tag; `tool:ppp_run` → `ppp_run`. */
export function subjectName(subjectId: string): { name: string; kind: PulseSubjectKind | null; server: string | null } {
  const at = subjectId.indexOf(':');
  if (at < 0) return { name: subjectId, kind: null, server: null };
  const kind = subjectId.slice(0, at) as PulseSubjectKind;
  const rest = subjectId.slice(at + 1);
  if (kind === 'mcp') {
    const slash = rest.indexOf('/');
    return slash >= 0 ? { name: rest.slice(slash + 1), kind, server: rest.slice(0, slash) } : { name: rest, kind, server: null };
  }
  return { name: rest, kind: kind in SUBJECT_KIND_LABELS ? kind : null, server: null };
}

// ------------------------------------------------------------------------------------------------ the record (section 2, 14.1)

/** Section 15.1: what a failing tool call carried: the failure's message and rule, and the model-facing sentence. */
export interface PulseToolFailureDetail { message: string | null; rule: string | null; hint: string | null }

export interface PulseToolCall {
  name: string; server?: string; mcpTool?: string;
  decision: 'read' | 'proposed' | 'auto-executed' | 'denied' | 'invalid' | 'blocked-by-risk' | 'error';
  ok: boolean; reason?: string; code?: string; ms: number; kind: 'read' | 'write-auto' | 'write-confirmed';
  detail?: PulseToolFailureDetail;
}

/**
 * Section 15.2: the PC's failure bundle as the record keeps it (the newest three per ask), scrubbed twice and carrying no
 * user, machine or device: the code and message, the runner's envelope, the MCP server's stderr tail, the server's health
 * at the time, the versions (coarse: agent and pyRevit major.minor, Revit its year, Windows its major). No line of the
 * agent's own log (PR #698 review NH1): Support keeps those under the PC's identity in its desktop logs.
 */
export interface PulseFailureBundle {
  tool: string; server?: string; mcpTool?: string;
  code: string; message: string;
  runner?: { mode?: string; title?: string; exception?: string; traceback?: string; warnings?: string[]; changeSummary?: unknown; elementCount?: number; rolledBack?: boolean };
  serverStderrTail?: string[];
  mcpHealth?: { status: string; errorCode?: string; uptimePercent24h?: number };
  versions: { agent: string; server?: string; revit?: string; pyRevit?: string; windows?: string };
  /** The hour the bundle reached the service (ms, floored to the hour); no call or action id, no PC clock. */
  atHour: number;
}

/** A subject's most frequent normalised error text in the 24-hour window, with its count (section 15.2). */
export interface PulseTopError { text: string; count: number }

/** A record's action: its own random key (never the pending store's action id), and times floored to the hour. */
export interface PulseAction {
  key: string; tool: string; highProfile: boolean;
  status: 'done' | 'failed' | 'reconciliation-required' | 'cancelled' | 'expired' | 'pending';
  settledMs: number | null; errorCode?: string;
  proposedAtMs?: number; confirmedAtMs?: number | null;
}

export interface PulseFeedback {
  kind: 'card' | 'thumb'; actionKey?: string; rating?: PulseRating; undo?: boolean; thumb?: 'up' | 'down';
  tags?: string[]; comment: string | null; atMs: number; reviewedAt: number | null; problemId: string | null;
}

/** A record: no trace id, `ts` to the hour, durations as bucket edges, cost and tokens as bucket midpoints (owner, 2026-10-23 22:29 PT). */
export interface PulseRecord {
  schemaVersion: 1;
  askId: string; episodeId: string; ts: number; durationMs: number;
  surface: string; domain: PulseDomain; intent: string; intentKey: string;
  questionText: string | null; answerText: string | null;
  outcome: PulseOutcome; outcomeReason: PulseOutcomeReason | null;
  saidBy: 'said-cannot' | 'tried-and-failed' | null;
  outcomeBy: 'rules' | 'judge' | 'pending';
  judgeConfidence: number | null; judgeModel: string | null;
  toolCalls: PulseToolCall[]; actions: PulseAction[];
  model: string; rounds: number; costUsd: number; stopReason: string;
  feedback: PulseFeedback | null;
  signals: {
    claimedFileNoneSaved: boolean; repeatInEpisode: boolean; rephraseInEpisode: boolean; abandonedEpisode: boolean;
    refusalStop: boolean; pendingExpired: boolean; codeNonZeroExit: boolean; knowledgeNoSource?: boolean; likelyCompanyQuestion?: boolean;
  };
  healthAtTime: Array<{ subjectId: string; state: PulseHealthState }>;
  redactions: { secrets: number; emails: number; phones: number; names: number; addresses: number; ids: number; paths: number };
  textErasedAt: number | null;
  /** Section 15.2: the PC's bundles for this ask's failed device calls, newest first; null once the text is erased. */
  failureDiagnostics?: PulseFailureBundle[] | null;
  // Section 14.1
  ttftMs?: number | null; knowledgeMode?: 'work' | 'world' | null; sources?: number;
  modelCalls?: { attempts: number; failed: number; fallback: boolean; providerErrors: string[] };
  cacheReadTokens?: number; cacheWriteTokens?: number; inputTokens?: number; outputTokens?: number; outputCut?: boolean;
}

// ------------------------------------------------------------------------------------------------ the admin routes (section 6.3)

/** Every list answers `{ items, next, total }`; `next` is the cursor of the following page, or null. */
export interface PulseList<T> { items: T[]; next: string | null; total: number }

/** GET /overview: the tiles, the bars, the top ten, the needs-a-look counts, today's digest, the spend. */
export interface PulseOverview {
  range: { days: number; from: string; to: string };
  asks: { total: number; previous: number | null };
  helped: { pct: number; done: number; doneWithGaps: number };
  couldNot: { pct: number; count: number; saidCannotPct: number | null };
  feedback: { score: number | null; cards: number; workedPct: number | null };
  failingNow: Array<{ subjectId: string; since: number }>;
  helpedByDomain: Array<{ domain: PulseDomain; asks: number; helpedPct: number }>;
  notPossible: Array<{ intentKey: string; intent: string; domain: PulseDomain; asks: number; reason: PulseOutcomeReason; repeats: number }>;
  needsALook: { openProblems: number; newProblemsToday: number; feedbackUnreviewed: number; feedbackUndo: number; unknownPct: number; spendTodayUsd: number; budgetUsd: number };
  digest: { id: string; kind: 'daily' | 'weekly'; createdAt: number; model: string; summary: string } | null;
}

/** GET /asks: one row per intent. */
export interface PulseIntentRow {
  intentKey: string; intent: string; domain: PulseDomain; asks: number; helpedPct: number; couldNotPct: number; repeatRate: number;
  trend: number[]; feedbackScore: number | null; lastSeen: number;
}

/** GET /asks/:intentKey: the drawer. */
export interface PulseIntentDetail {
  intentKey: string; intent: string; domain: PulseDomain; asks: number;
  outcomes: Partial<Record<PulseOutcome, number>>;
  reasons: Record<string, number>;
  tools: Array<{ subjectId: string; calls: number; okPct: number; state: PulseHealthState }>;
  samples: Array<{ askId: string; ts: number; surface: string; outcome: PulseOutcome; questionText: string | null; answerText: string | null }>;
  comments: Array<{ key: string; atMs: number; rating?: PulseRating; thumb?: 'up' | 'down'; undo?: boolean; comment: string }>;
  problemIds: string[];
}

/** GET /domains/:domain: the scorecard (strategy 9.3, mockup admin-8). */
export interface PulseDomainScorecard {
  domain: PulseDomain; range: { days: number; from: string; to: string }; asks: number;
  outcomes: Partial<Record<PulseOutcome, number>>;
  saidCannot: number; triedFailed: number;
  feedback: { cards: number; worked: number; partly: number; failed: number; undo: number; tags: Record<string, number> };
  reasons: Record<string, number>;
  intents: Array<{ intentKey: string; intent: string; asks: number; helpedPct: number; trend: number[] }>;
  tools: Array<{ subjectId: string; successPct: number; p95Ms: number; state: PulseHealthState; since: number | null; topError?: PulseTopError | null }>;
  /** The desktop roll-up (section 6.2), for `gyro-ai-for-revit` and `bluebeam-revu` only. */
  mcp: { server: string; reachablePct: number; devices: number } | null;
}

/** GET /subjects: tools & connectors. */
export interface PulseSubjectRow {
  subjectId: string; kind: PulseSubjectKind; state: PulseHealthState; since: number | null; successPct: number; p95Ms: number;
  calls24h: number; topCode: string | null; spark: number[]; problemId: string | null;
  /** Section 15.2: the most frequent error text beside the code, when the PCs' bundles gave one. */
  topError?: PulseTopError | null;
  muted?: boolean; quarantinedFlag?: string | null;
  /** The numbers the health rules are judged on, when the service sends them. */
  failPct?: number; consecutiveFailures?: number; medianMs7d?: number | null; notConnected?: number;
}

/** GET /feedback: one row per entry (the inbox). */
export interface PulseFeedbackRow {
  key: string; askId: string; actionKey?: string; atMs: number; surface: string; domain: PulseDomain; intentKey: string; intent: string;
  tool: string | null; kind: 'card' | 'thumb'; rating?: PulseRating; undo?: boolean; thumb?: 'up' | 'down'; tags: string[];
  comment: string | null; outcome: PulseOutcome; outcomeBy?: 'rules' | 'judge' | 'pending';
  healthAtTime?: PulseHealthState | null; episode?: string | null; action?: string | null;
  reviewedAt: number | null; problemId: string | null;
}

/** Section 9.3. */
export interface PulseProblem {
  problemId: string; openedAt: number; openedBy: 'health' | 'digest' | 'admin';
  title: string; domain: PulseDomain; subjectIds: string[]; intentKeys: string[];
  symptoms: { codes: Record<string, number>; reasons: Record<string, number>; tags: Record<string, number>; asks: number; firstSeen: number; lastSeen: number; spark: number[]; topErrors?: PulseTopError[] };
  evidence: Array<{ kind: 'record' | 'feedback' | 'health'; id: string }>;
  likelyCause: string | null; codeArea: string | null; suggestedFix: string | null; wouldProve: string | null;
  status: PulseProblemStatus; fixedInVersion: string | null; duplicateOf: string | null; assignee: string | null;
  notes: Array<{ at: number; by: string; text: string }>;
  lab: { reportId: string | null; gheIssueUrl: string | null; filedAt: number | null; error: string | null };
  verification: {
    startedAt: number; endsAt: number; before: { asks: number; failPct: number; undo: number };
    after: { asks: number; failPct: number; undo: number } | null; verdict: 'verified' | 'not-fixed' | null;
  } | null;
  /** The list's sort key, when the service sends it: asks × failure rate. */
  impact?: number;
  /** The subjects' health now, when the service sends it. */
  subjectStates?: Record<string, PulseHealthState>;
}

/** Section 9.2. */
export interface PulseDigest {
  id: string; kind: 'daily' | 'weekly'; from: string; to: string; createdAt: number; model: string; promptVersion: string;
  asked: Array<{ domain: PulseDomain; intentKey: string; asks: number; helpedPct: number; new: boolean }>;
  notWorking: Array<{ intentKey: string; domain: PulseDomain; asks: number; couldNotPct: number; saidCannot: number; triedFailed: number; undo: number; samples: string[]; recordIds: string[] }>;
  notPossible: Array<{ intentKey: string; domain: PulseDomain; asks: number; reason: PulseOutcomeReason; repeats: number; rephrases: number; recordIds: string[] }>;
  health: Array<{ subjectId: string; from: PulseHealthState; to: PulseHealthState; at: number; problemId: string | null }>;
  feedbackWorthReading: Array<{ theme: string; quotes: string[]; feedbackKeys: string[] }>;
  suggestedChanges: Array<{ title: string; kind: 'add-tool' | 'wire-connector' | 'guidance' | 'limit' | 'bug' | 'other'; why: string; evidence: string[]; confidence: 'likely' | 'possible' | 'unclear'; problemId: string | null }>;
  pulseQuality: { unknownPct: number; judgeConfidenceP50: number; cardsShown: number; cardsAnswered: number; spendUsd: number };
  forDevTeam?: string;
  trends?: Array<{ domain: PulseDomain; helpedPctByWeek: number[] }>;
  topTen?: Array<{ intentKey: string; score: number; why: string }>;
  readAt: number | null;
}

/** A digest as the list sends it: the full digest, or its head alone (the page reads the rest when it is opened). */
export type PulseDigestListItem = Pick<PulseDigest, 'id' | 'kind' | 'from' | 'to' | 'createdAt' | 'model' | 'readAt'> & Partial<PulseDigest>;

/** Section 14.3: `GET /operations`, the five metrics rolled over the range. */
export interface PulseSpeedRow { n: number; ttftSum: number; ttftP95: number | null; durSum: number; durP95: number | null }
export interface PulseFunnelRow { proposed: number; confirmed: number; done: number; failed: number; expired: number; cancelled: number; confirmMsSum: number; confirmMsP95: number | null }
export interface PulseModelRow { calls: number; failed: number; fallbacks: number; outputCut: number; inputTokens: number; outputTokens: number; cacheRead: number; cacheWrite: number; providerErrors: Record<string, number> }
export interface PulseOperations {
  cost: Record<string, Partial<Record<PulseOutcome, { asks: number; usd: number }>>>;
  /** The list under the cost table: the could-not and done-with-gaps intents that cost the most (14.3). */
  unhelpfulIntents?: Array<{ intentKey: string; intent: string; domain: PulseDomain; asks: number; usd: number }>;
  speed: { surface: Record<string, PulseSpeedRow>; domain: Record<string, PulseSpeedRow>; tool: Record<string, PulseSpeedRow> };
  funnel: Record<string, PulseFunnelRow>;
  models: Record<string, PulseModelRow>;
  knowledge: {
    workAsks: number; worldAsks: number; noSource: number; likelyCompanyInWorld: number;
    /** By intent (14.3): the asks whose retrieval found nothing, and the World IQ asks that look like company questions. */
    noSourceIntents?: Array<{ intentKey: string; intent: string; asks: number; samples?: string[] }>;
    likelyCompanyIntents?: Array<{ intentKey: string; intent: string; asks: number; samples: string[] }>;
  };
}

// ------------------------------------------------------------------------------------------------ settings (section 11)

export interface PulseSettings {
  cardsPerDevicePerDay: number;
  highProfileTools: string[];
  retentionDays: number;
  keepAnswerText: boolean;
  lockMode: PulseLockMode;
  /** Whether a second call to a failing tool inside one answer is refused (section 8.3); an older service may not report it. */
  refuseSecondCall?: boolean;
  thresholds: { degradedPct: number; failingPct: number; failingMinCalls: number; consecutive: number; latencyX: number };
  judgeFloor: number;
  models: { judge: 'cheap' | 'strong'; digest: 'cheap' | 'strong' };
  dailyBudgetUsd: number;
  digestDailyAt: string;
  digestWeeklyDay: string;
  timezone: string;
  problemMinAsks: number;
  problemMinFailPct: number;
  lab: { enabled: boolean; app: string };
  notifyDevelopers: boolean;
  employeeIdPattern: string | null;
}

export type PulseSettingsPatch = Partial<PulseSettings>;

/** One job's last run (section 9.4), as Settings shows it. */
export interface PulseJobRun { lastRunAt: number | null; result: 'ok' | 'failed' | 'skipped' | null; note?: string | null; nextRunAt?: number | null }

/** GET /settings: the admin probe's answer too (404 `pulse_disabled` while the flag is off). */
export interface PulseSettingsView {
  settings: PulseSettings;
  defaults: PulseSettings;
  flags: { pulse: boolean; cards: boolean };
  /** The jobs' last runs by name (`daily-digest`, `weekly-digest`, `retention-sweep`, `verification`), when reported. */
  jobs?: Record<string, PulseJobRun>;
}

export const PULSE_DEFAULTS: PulseSettings = {
  cardsPerDevicePerDay: 10, highProfileTools: [], retentionDays: 180, keepAnswerText: true, lockMode: 'warn', refuseSecondCall: true,
  thresholds: { degradedPct: 10, failingPct: 40, failingMinCalls: 8, consecutive: 5, latencyX: 3 },
  judgeFloor: 0.6, models: { judge: 'cheap', digest: 'strong' }, dailyBudgetUsd: 15,
  digestDailyAt: '05:00', digestWeeklyDay: 'monday', timezone: 'America/Los_Angeles',
  problemMinAsks: 20, problemMinFailPct: 50, lab: { enabled: false, app: 'gyro-assistant' }, notifyDevelopers: false, employeeIdPattern: null,
};

/** The jobs of section 9.4, in the order Settings lists them, with their plain names. */
export const PULSE_JOBS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'daily-digest', label: 'Daily digest' },
  { key: 'weekly-digest', label: 'Weekly digest' },
  { key: 'retention-sweep', label: 'Retention sweep' },
  { key: 'verification', label: 'Fix verification' },
];

/** The form: numbers as typed, the tool list one per line, the rest as chosen. */
export interface PulseSettingsForm {
  cardsPerDevicePerDay: string;
  highProfileTools: string;
  retentionDays: string;
  keepAnswerText: boolean;
  lockMode: PulseLockMode;
  refuseSecondCall: boolean;
  degradedPct: string;
  failingPct: string;
  failingMinCalls: string;
  consecutive: string;
  latencyX: string;
  judgeFloor: string;
  judgeModel: 'cheap' | 'strong';
  digestModel: 'cheap' | 'strong';
  dailyBudgetUsd: string;
  digestDailyAt: string;
  digestWeeklyDay: string;
  timezone: string;
  problemMinAsks: string;
  problemMinFailPct: string;
  labEnabled: boolean;
  labApp: string;
  notifyDevelopers: boolean;
  employeeIdPattern: string;
}

type PulseNumberKey = 'cardsPerDevicePerDay' | 'retentionDays' | 'degradedPct' | 'failingPct' | 'failingMinCalls' | 'consecutive' | 'latencyX' | 'judgeFloor' | 'dailyBudgetUsd' | 'problemMinAsks' | 'problemMinFailPct';

export interface PulseNumberField {
  key: PulseNumberKey;
  /** The plain name on screen (never the setting's identifier). */
  label: string;
  hint: string;
  bounds: { min: number; max: number };
  integer: boolean;
  /** The Settings panel the field sits in. */
  group: 'cards' | 'kept' | 'thresholds' | 'analyst';
}

export const PULSE_NUMBER_FIELDS: readonly PulseNumberField[] = [
  { key: 'cardsPerDevicePerDay', label: 'Feedback cards per PC per day', bounds: { min: 0, max: 100 }, integer: true, group: 'cards', hint: 'Default 10. 0 shows no card.' },
  { key: 'retentionDays', label: 'Days the question and answer text is kept', bounds: { min: 1, max: 730 }, integer: true, group: 'kept', hint: 'Default 180 (the owner, D4). Counts are kept for good; text older than this is erased by the nightly sweep.' },
  { key: 'degradedPct', label: 'Degraded from this failure rate (%)', bounds: { min: 1, max: 99 }, integer: false, group: 'thresholds', hint: 'Default 10.' },
  { key: 'failingPct', label: 'Failing above this failure rate (%)', bounds: { min: 1, max: 100 }, integer: false, group: 'thresholds', hint: 'Default 40.' },
  { key: 'failingMinCalls', label: 'Calls needed before failing is called', bounds: { min: 1, max: 1000 }, integer: true, group: 'thresholds', hint: 'Default 8.' },
  { key: 'consecutive', label: 'Failures in a row that mean failing', bounds: { min: 2, max: 100 }, integer: true, group: 'thresholds', hint: 'Default 5, with one reason.' },
  { key: 'latencyX', label: 'Slowness that means degraded (× the 7-day median p95)', bounds: { min: 1.1, max: 20 }, integer: false, group: 'thresholds', hint: 'Default 3.' },
  { key: 'judgeFloor', label: 'Judge confidence floor (0 to 1)', bounds: { min: 0, max: 1 }, integer: false, group: 'analyst', hint: 'Default 0.6. Below it the outcome stays unknown.' },
  { key: 'dailyBudgetUsd', label: 'Daily budget for the judge and the analyst (USD)', bounds: { min: 0, max: 1000 }, integer: false, group: 'analyst', hint: 'Default 15 (the owner, D7). The judge yields first, then the daily digest.' },
  { key: 'problemMinAsks', label: 'Asks in 7 days before the analyst opens a problem', bounds: { min: 1, max: 10_000 }, integer: true, group: 'analyst', hint: 'Default 20.' },
  { key: 'problemMinFailPct', label: 'Share not done that opens a problem (%)', bounds: { min: 1, max: 100 }, integer: false, group: 'analyst', hint: 'Default 50.' },
];

/** The lock mode as one sentence (section 8.3): Warn is the only mode (the owner, D5); Pulse never switches a tool off. */
export const LOCK_MODE_OPTIONS: ReadonlyArray<{ value: PulseLockMode; label: string; sentence: string }> = [
  { value: 'warn', label: 'Warn', sentence: 'While a tool is failing, Gyro says so before it tries it and offers the way round. Nothing is switched off, ever: Pulse has no quarantine mode.' },
];

/**
 * What the Asks and records pages say about stored text (the owner, 2026-10-23: D2 "keep as built"; switch-on review N4).
 * The scrub removes e-mail addresses, account names (the part of an address before the @), the owner's name, phone numbers,
 * ids, paths and secrets. It does not remove a full name written in the text: anyone's may remain, Gyro users included.
 */
export const STORED_TEXT_NOTE = "Questions and answers are scrubbed of e-mail addresses, account names, the owner's name, phone numbers, ids, paths and secrets. Full names written in the text are not removed: anyone's name may remain, Gyro users included (the owner's decision: keep as built).";

/** A duration bucket in words (the record keeps the bucket's lower edge, never the exact time; switch-on review N1). */
export function durationBucketText(lowerEdgeMs: number | null | undefined): string {
  if (typeof lowerEdgeMs !== 'number' || !Number.isFinite(lowerEdgeMs) || lowerEdgeMs < 0) return '—';
  const edges: Array<[number, string]> = [[0, 'under 1 s'], [1_000, '1–2 s'], [2_000, '2–5 s'], [5_000, '5–10 s'], [10_000, '10–30 s'], [30_000, '30–60 s'], [60_000, '1–2 min'], [120_000, '2–5 min'], [300_000, 'over 5 min']];
  let label = edges[0]![1];
  for (const [edge, text] of edges) if (lowerEdgeMs >= edge) label = text;
  return label;
}

export const MODEL_CHOICES: ReadonlyArray<{ value: 'cheap' | 'strong'; label: string }> = [
  { value: 'cheap', label: "The catalog's cheap model" },
  { value: 'strong', label: "The catalog's strong model" },
];

export const WEEKDAY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'monday', label: 'Monday' }, { value: 'tuesday', label: 'Tuesday' }, { value: 'wednesday', label: 'Wednesday' }, { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' }, { value: 'saturday', label: 'Saturday' }, { value: 'sunday', label: 'Sunday' },
];

const trimNumber = (value: number): string => String(Math.round(value * 1000) / 1000);

export function pulseForm(settings: PulseSettings): PulseSettingsForm {
  return {
    cardsPerDevicePerDay: String(settings.cardsPerDevicePerDay),
    highProfileTools: settings.highProfileTools.join('\n'),
    retentionDays: String(settings.retentionDays),
    keepAnswerText: settings.keepAnswerText,
    lockMode: settings.lockMode,
    refuseSecondCall: settings.refuseSecondCall ?? true,
    degradedPct: trimNumber(settings.thresholds.degradedPct),
    failingPct: trimNumber(settings.thresholds.failingPct),
    failingMinCalls: String(settings.thresholds.failingMinCalls),
    consecutive: String(settings.thresholds.consecutive),
    latencyX: trimNumber(settings.thresholds.latencyX),
    judgeFloor: trimNumber(settings.judgeFloor),
    judgeModel: settings.models.judge,
    digestModel: settings.models.digest,
    dailyBudgetUsd: trimNumber(settings.dailyBudgetUsd),
    digestDailyAt: settings.digestDailyAt,
    digestWeeklyDay: settings.digestWeeklyDay,
    timezone: settings.timezone,
    problemMinAsks: String(settings.problemMinAsks),
    problemMinFailPct: trimNumber(settings.problemMinFailPct),
    labEnabled: settings.lab.enabled,
    labApp: settings.lab.app,
    notifyDevelopers: settings.notifyDevelopers,
    employeeIdPattern: settings.employeeIdPattern ?? '',
  };
}

const numberOf = (text: string): number => Number(text.trim().replace(/,/g, ''));
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The first problem with the form, naming the field, or null. Nothing is sent while there is one. */
export function validatePulse(form: PulseSettingsForm): string | null {
  for (const field of PULSE_NUMBER_FIELDS) {
    const text = form[field.key].trim();
    const value = numberOf(text);
    if (!text || !Number.isFinite(value)) return `${field.label}: enter a number.`;
    if (field.integer && !Number.isInteger(value)) return `${field.label}: enter a whole number.`;
    if (value < field.bounds.min || value > field.bounds.max) {
      return `${field.label}: enter ${field.bounds.min.toLocaleString('en-US')} to ${field.bounds.max.toLocaleString('en-US')}.`;
    }
  }
  if (numberOf(form.degradedPct) >= numberOf(form.failingPct)) return 'Degraded from this failure rate (%): it must be below the failing rate.';
  if (!TIME_OF_DAY.test(form.digestDailyAt.trim())) return 'Daily digest time: enter a time as HH:MM, 24-hour.';
  if (!form.timezone.trim()) return 'Time zone: enter an IANA zone such as America/Los_Angeles.';
  if (!form.labApp.trim()) return 'The Lab app: enter the app key the reports are filed under.';
  if (form.employeeIdPattern.trim()) {
    try {
      new RegExp(form.employeeIdPattern.trim());
    } catch {
      return 'Employee id pattern: enter a valid regular expression, or leave it empty.';
    }
  }
  return null;
}

/** The tool names typed one per line (or comma-separated), trimmed, deduplicated, in order. */
export function toolList(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\n,]/)) {
    const name = raw.trim();
    if (name && !seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  }
  return out;
}

/** Only what changed, in the service's shapes. Call after validatePulse. */
export function pulsePatch(form: PulseSettingsForm, stored: PulseSettings): PulseSettingsPatch {
  const next: PulseSettings = {
    cardsPerDevicePerDay: numberOf(form.cardsPerDevicePerDay),
    highProfileTools: toolList(form.highProfileTools),
    retentionDays: numberOf(form.retentionDays),
    keepAnswerText: form.keepAnswerText,
    lockMode: form.lockMode,
    refuseSecondCall: form.refuseSecondCall,
    thresholds: {
      degradedPct: numberOf(form.degradedPct), failingPct: numberOf(form.failingPct), failingMinCalls: numberOf(form.failingMinCalls),
      consecutive: numberOf(form.consecutive), latencyX: numberOf(form.latencyX),
    },
    judgeFloor: numberOf(form.judgeFloor),
    models: { judge: form.judgeModel, digest: form.digestModel },
    dailyBudgetUsd: numberOf(form.dailyBudgetUsd),
    digestDailyAt: form.digestDailyAt.trim(),
    digestWeeklyDay: form.digestWeeklyDay,
    timezone: form.timezone.trim(),
    problemMinAsks: numberOf(form.problemMinAsks),
    problemMinFailPct: numberOf(form.problemMinFailPct),
    lab: { enabled: form.labEnabled, app: form.labApp.trim() },
    notifyDevelopers: form.notifyDevelopers,
    employeeIdPattern: form.employeeIdPattern.trim() || null,
  };
  const patch: PulseSettingsPatch = {};
  // A service that does not report the refusal switch yet reads as "on", so an untouched form sends nothing for it.
  const was: PulseSettings = { ...stored, refuseSecondCall: stored.refuseSecondCall ?? true };
  for (const key of Object.keys(next) as Array<keyof PulseSettings>) {
    if (JSON.stringify(next[key]) !== JSON.stringify(was[key])) (patch as Record<string, unknown>)[key] = next[key];
  }
  return patch;
}

// ------------------------------------------------------------------------------------------------ lists: query, sort, export

export const RANGE_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

export const DEFAULT_RANGE_DAYS = 7;

/**
 * The query string for a Pulse list: every set value, in the order given, as the routes take them (`days`, `domain`,
 * `surface`, `q`, `sort`, `cursor`, `limit`, and a route's own filters); nothing when every value is empty. A `true`
 * is `1` (the routes' `undo=1`, `comment=1`); a `false` is left out.
 */
export function pulseQuery(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, value === true ? '1' : String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export interface SortState { key: string; direction: 'asc' | 'desc' }

/** The route's `sort` value: the column, `-` first for descending. */
export function sortParam(sort: SortState | null | undefined): string | undefined {
  if (!sort) return undefined;
  return sort.direction === 'desc' ? `-${sort.key}` : sort.key;
}

/** `-asks` → asks, descending. */
export function parseSort(sort: string): SortState {
  return sort.startsWith('-') ? { key: sort.slice(1), direction: 'desc' } : { key: sort, direction: 'asc' };
}

/**
 * The next sort after a click on `key`: the same column flips; another column starts with its own default (descending
 * for a number or a date, so the largest and the newest come first; ascending for words).
 */
export function toggleSort(current: SortState | null, key: string, numeric = true): SortState {
  if (current && current.key === key) return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  return { key, direction: numeric ? 'desc' : 'asc' };
}

/** Sorts a list on the client by a column's value (a small list the service does not page, such as a scorecard's). */
export function sortBy<T>(rows: readonly T[], sort: SortState | null, value: (row: T, key: string) => string | number | null | undefined): T[] {
  if (!sort) return [...rows];
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a, sort.key);
    const y = value(b, sort.key);
    if (x === y) return 0;
    if (x === null || x === undefined) return 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    return String(x).localeCompare(String(y), 'en-US', { sensitivity: 'base' }) * sign;
  });
}

export type PulseExportList = 'records' | 'feedback' | 'problems' | 'digests' | 'operations';

/** `GET /v1/admin/pulse/export?list=…` with the list's filters, the audit export's pattern (lib/governance.ts auditExportHref). */
export function pulseExportHref(list: PulseExportList, filter: Record<string, string | number | boolean | null | undefined> = {}): string {
  const params = new URLSearchParams({ list });
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    if (key === 'cursor' || key === 'limit') continue;
    params.set(key, value === true ? '1' : String(value));
  }
  return `/v1/admin/pulse/export?${params.toString()}`;
}

// ------------------------------------------------------------------------------------------------ links between the pages

/** Where a claim's evidence opens: the record, the feedback entry, the subject, the intent, the problem, the domain. */
export const pulseHref = {
  record: (askId: string) => `/pulse-asks?record=${encodeURIComponent(askId)}`,
  intent: (intentKey: string) => `/pulse-asks?intent=${encodeURIComponent(intentKey)}`,
  domain: (domain: string) => `/pulse-domains?domain=${encodeURIComponent(domain)}`,
  subject: (subjectId: string) => `/pulse-tools?subject=${encodeURIComponent(subjectId)}`,
  feedback: (key: string) => `/pulse-feedback?key=${encodeURIComponent(key)}`,
  problem: (problemId: string) => `/pulse-problems?problem=${encodeURIComponent(problemId)}`,
  digest: (digestId: string) => `/pulse-digests?digest=${encodeURIComponent(digestId)}`,
};

// ------------------------------------------------------------------------------------------------ words and figures

/** `81 %` from a 0 to 100 figure (the mockups' spelling, with the space); an em dash for nothing. */
export function pctText(value: number | null | undefined, digits = 0): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)} %`;
}

/** A duration in plain units: `300 ms`, `1.4 s`, `21 s`, `2.5 min`. */
export function msText(ms: number | null | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

/** `$3.80`; a figure under a cent keeps four decimals so a small cost never reads as free; whole dollars over $100. */
export function usdText(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs > 0 && abs < 0.01) return `${sign}$${abs.toFixed(4)}`;
  if (abs >= 100) return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  return `${sign}$${abs.toFixed(2)}`;
}

/** `+6 % vs the week before` (or the period's own name); null without a previous figure. */
export function deltaText(current: number, previous: number | null | undefined, period = 'the period before'): string | null {
  if (typeof previous !== 'number' || !Number.isFinite(previous) || previous <= 0) return null;
  const delta = Math.round(((current - previous) / previous) * 100);
  return `${delta > 0 ? '+' : ''}${delta} % vs ${period}`;
}

export function periodName(days: number): string {
  if (days === 7) return 'the week before';
  if (days === 30) return 'the 30 days before';
  if (days === 90) return 'the 90 days before';
  return `the ${days} days before`;
}

const dayKey = (date: Date): string => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
const clock = (date: Date): string => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `Today 14:02`, `Yesterday 13:40`, `Oct 21 09:31` (the viewer's clock). */
export function whenText(ts: number | null | undefined, now = Date.now()): string {
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return '—';
  const date = new Date(ts);
  const today = new Date(now);
  const yesterday = new Date(now - 86_400_000);
  if (dayKey(date) === dayKey(today)) return `Today ${clock(date)}`;
  if (dayKey(date) === dayKey(yesterday)) return `Yesterday ${clock(date)}`;
  const year = date.getFullYear() === today.getFullYear() ? '' : ` ${date.getFullYear()}`;
  return `${MONTHS[date.getMonth()]} ${date.getDate()}${year} ${clock(date)}`;
}

/** `since 10:12` today, `since yesterday 18:40`, `since Oct 21` before that. */
export function sinceText(ts: number | null | undefined, now = Date.now()): string {
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return '';
  const date = new Date(ts);
  const today = new Date(now);
  if (dayKey(date) === dayKey(today)) return `since ${clock(date)}`;
  if (dayKey(date) === dayKey(new Date(now - 86_400_000))) return `since yesterday ${clock(date)}`;
  return `since ${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/** The engine's split as a share of asks: 734 of 1,204 → 61 (a percentage, 0 when there were no asks). */
export function shareOf(part: number | null | undefined, total: number | null | undefined): number {
  if (!part || !total || total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

/** `{ no_tool: 48, refused: 31 }` → rows, largest first. */
export function countRows(counts: Record<string, number> | null | undefined): Array<{ key: string; count: number }> {
  if (!counts) return [];
  return Object.entries(counts).filter(([, n]) => typeof n === 'number' && n > 0).map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);
}

/** A problem's weight for the list: asks × the share not done (the service's `impact` when it sends one). */
export function problemImpact(problem: Pick<PulseProblem, 'symptoms' | 'impact'>): number {
  if (typeof problem.impact === 'number') return problem.impact;
  const reasons = Object.values(problem.symptoms.reasons ?? {}).reduce((sum, n) => sum + n, 0);
  const failPct = problem.symptoms.asks > 0 ? Math.min(1, reasons / problem.symptoms.asks) : 0;
  return Math.round(problem.symptoms.asks * failPct);
}

/** `23 asks · 19 × mcp_timeout, 4 × mcp_tool_error · 4 "Didn't work" cards`, as the problem drawer reads them. */
export function symptomsText(symptoms: PulseProblem['symptoms']): string {
  const parts = [`${symptoms.asks.toLocaleString('en-US')} asks`];
  const codes = countRows(symptoms.codes).map((row) => `${row.count} × ${row.key}`);
  if (codes.length) parts.push(codes.join(', '));
  const reasons = countRows(symptoms.reasons).map((row) => `${row.count} × ${reasonLabel(row.key)}`);
  if (!codes.length && reasons.length) parts.push(reasons.join(', '));
  const tags = countRows(symptoms.tags);
  if (tags.length) parts.push(`${tags.reduce((sum, row) => sum + row.count, 0)} "Didn't work" cards (${tags.map((row) => `${row.key} ${row.count}`).join(', ')})`);
  return parts.join(' · ');
}

/** The evidence list counted by kind: `23 records · 4 feedback entries · 1 health transition`. */
export function evidenceText(evidence: PulseProblem['evidence']): string {
  const n = (kind: 'record' | 'feedback' | 'health') => evidence.filter((item) => item.kind === kind).length;
  const word = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  return [word(n('record'), 'record', 'records'), word(n('feedback'), 'feedback entry', 'feedback entries'), word(n('health'), 'health transition', 'health transitions')].join(' · ');
}

/** The Lab line under a problem (owner, D9). */
export function labLineText(lab: PulseProblem['lab'], labEnabled = true): string {
  if (lab.reportId) {
    return `Filed to The Lab as ${lab.reportId} (Gyro Assistant); ${lab.gheIssueUrl ? 'The Lab posted the GHE issue' : 'The Lab posts the GHE issue'}. Notes and status changes are audited.`;
  }
  if (lab.error) return `The Lab refused the report: ${lab.error}. The problem is kept here; the filing is retried on the next status change. Notes and status changes are audited.`;
  if (!labEnabled) return 'Not filed to The Lab: the Lab filing switch is off under Pulse › Settings. Notes and status changes are audited.';
  return 'Not filed to The Lab yet. Notes and status changes are audited.';
}

/** Who opened the file, in words. */
export function openedByText(openedBy: PulseProblem['openedBy']): string {
  return openedBy === 'health' ? 'the health engine' : openedBy === 'digest' ? 'the analyst' : 'an admin';
}

/**
 * The health rules (section 8.2) as sentences with the thresholds in force; with a subject, a last sentence with its
 * numbers now.
 */
export function healthRuleSentences(settings: Pick<PulseSettings, 'thresholds' | 'lockMode'> & { refuseSecondCall?: boolean }, subject?: PulseSubjectRow | null): string[] {
  const t = settings.thresholds;
  const lock = LOCK_MODE_OPTIONS.find((option) => option.value === settings.lockMode)?.label ?? settings.lockMode;
  const refusal = settings.refuseSecondCall === false
    ? 'A second call to a failing tool inside one answer is not refused (the switch under Pulse › Settings is off).'
    : "A second call to a failing tool inside one answer is refused; Pulse's own refusals never count as failures.";
  const lines = [
    `Degraded when ${trimNumber(t.degradedPct)} % to ${trimNumber(t.failingPct)} % of its calls fail over the window (at least 5 calls), when its p95 time is over ${trimNumber(t.latencyX)} × its 7-day median, or when a connected app answers partially.`,
    `Failing when more than ${trimNumber(t.failingPct)} % of at least ${t.failingMinCalls} calls fail, after ${t.consecutive} failures in a row with one reason, when a connected app fails or answers empty, or when every call in the last hour failed (at least 3); and only when the failures came from at least 2 different people (one person alone, on any number of PCs, can mark it degraded, never failing).`,
    `Nothing is ever switched off: the lock mode is ${lock}, the only one. ${refusal} A tool refused by a person's own setting, not connected or without consent, given bad input by the model, or asked for something not found, counts separately and never as a failure.`,
    'Recovering once its last 10 calls succeeded after failing, or at once when an admin presses Release; healthy again after 24 clean hours. Mute keeps measuring but stops telling the model and stops the refusal.',
  ];
  if (subject) {
    const parts: string[] = [];
    if (typeof subject.failPct === 'number') parts.push(`${trimNumber(subject.failPct)} % of ${subject.calls24h.toLocaleString('en-US')} calls failed in 24 h`);
    else parts.push(`${subject.calls24h.toLocaleString('en-US')} calls in 24 h, ${trimNumber(subject.successPct)} % ok`);
    parts.push(`p95 ${msText(subject.p95Ms)}${typeof subject.medianMs7d === 'number' ? ` (7-day median ${msText(subject.medianMs7d)})` : ''}`);
    if (typeof subject.consecutiveFailures === 'number') parts.push(`${subject.consecutiveFailures} in a row`);
    if (subject.topCode) parts.push(`top code ${subject.topCode}`);
    if (subject.topError) parts.push(topErrorText(subject.topError));
    lines.push(`${subjectName(subject.subjectId).name} now: ${parts.join(' · ')}.`);
  }
  return lines;
}

/** `top error: Autodesk.Revit.Exceptions.InvalidOperationException: The family is not editable × 17`. */
export function topErrorText(top: PulseTopError | null | undefined): string {
  if (!top) return '';
  return `top error: ${top.text} × ${top.count.toLocaleString('en-US')}`;
}

/** `top errors: TimeoutException: render exceeded N s × 19 · Revu returned an empty result × 4`, for a problem's symptoms. */
export function topErrorsText(errors: PulseTopError[] | null | undefined): string {
  if (!errors?.length) return '';
  return `top errors: ${errors.map((error) => `${error.text} × ${error.count.toLocaleString('en-US')}`).join(' · ')}`;
}

/** `revit/join_walls · mcp_tool_error`: the bundle's title line. */
export function bundleTitle(bundle: Pick<PulseFailureBundle, 'tool' | 'server' | 'mcpTool' | 'code'>): string {
  return `${bundle.server ? `${bundle.server}/` : ''}${bundle.mcpTool ?? bundle.tool} · ${bundle.code}`;
}

/** `agent 0.4 · server 1.3.0 · Revit 2025 · pyRevit 5.0 · Windows 11` (the service keeps them coarse; review NM1). */
export function versionsText(versions: PulseFailureBundle['versions']): string {
  const parts = [`agent ${versions.agent}`];
  if (versions.server) parts.push(`server ${versions.server}`);
  if (versions.revit) parts.push(`Revit ${versions.revit}`);
  if (versions.pyRevit) parts.push(`pyRevit ${versions.pyRevit}`);
  if (versions.windows) parts.push(/^windows\b/i.test(versions.windows) ? versions.windows : `Windows ${versions.windows}`);
  return parts.join(' · ');
}

/** The bundle's health, elements and roll-back as one line: `MCP server reachable at the time, 87 % up in 24 h · 4 elements · rolled back`. */
export function bundleFootText(bundle: PulseFailureBundle): string {
  const parts: string[] = [];
  if (bundle.mcpHealth) {
    parts.push(`MCP server ${bundle.mcpHealth.status} at the time${bundle.mcpHealth.errorCode ? ` (${bundle.mcpHealth.errorCode})` : ''}${typeof bundle.mcpHealth.uptimePercent24h === 'number' ? `, ${trimNumber(bundle.mcpHealth.uptimePercent24h)} % up in 24 h` : ''}`);
  }
  if (typeof bundle.runner?.elementCount === 'number') parts.push(`${bundle.runner.elementCount.toLocaleString('en-US')} elements`);
  if (bundle.runner?.rolledBack) parts.push('rolled back');
  return parts.join(' · ');
}

/** The whole bundle as plain text, for Copy: everything the drawer shows, the collapsed parts included. */
export function bundleText(bundle: PulseFailureBundle): string {
  const lines: string[] = [`Failure diagnostics · ${bundleTitle(bundle)} · ${new Date(bundle.atHour).toISOString()} (the hour)`, bundle.message];
  if (bundle.runner) {
    const head = [bundle.runner.mode ? `mode ${bundle.runner.mode}` : '', bundle.runner.title ?? ''].filter(Boolean).join(' · ');
    lines.push('', `Runner${head ? ` (${head})` : ''}:`);
    if (bundle.runner.exception) lines.push(bundle.runner.exception);
    if (bundle.runner.traceback) lines.push(bundle.runner.traceback);
    if (bundle.runner.warnings?.length) lines.push('', 'Warnings:', ...bundle.runner.warnings.map((warning) => `- ${warning}`));
    if (bundle.runner.changeSummary !== undefined) lines.push('', `Change summary: ${JSON.stringify(bundle.runner.changeSummary)}`);
  }
  if (bundle.serverStderrTail?.length) lines.push('', `MCP server stderr (last ${bundle.serverStderrTail.length}):`, ...bundle.serverStderrTail);
  const foot = bundleFootText(bundle);
  lines.push('', `Versions: ${versionsText(bundle.versions)}`);
  if (foot) lines.push(foot);
  return lines.join('\n');
}

/**
 * No quarantine action (the owner, D5): Pulse never switches a tool off. Release ends a failing or degraded reading now (the
 * tool reads recovering, healthy after a clean day; switch-on review N7) and lifts a row an earlier version quarantined.
 */
export type PulseSubjectAction = 'release' | 'mute' | 'unmute';

/** What an admin may do to a subject in its state (section 8.4): the buttons the row offers. */
export function subjectActions(subject: Pick<PulseSubjectRow, 'state' | 'muted' | 'kind'>): PulseSubjectAction[] {
  const actions: PulseSubjectAction[] = [];
  if (subject.state === 'quarantined' || subject.state === 'failing' || subject.state === 'degraded') actions.push('release');
  actions.push(subject.muted ? 'unmute' : 'mute');
  return actions;
}

export const SUBJECT_ACTION_LABELS: Record<PulseSubjectAction, string> = { release: 'Release', mute: 'Mute', unmute: 'Unmute' };

/** The confirmation each subject action asks for, in plain words. */
export function subjectActionText(action: PulseSubjectAction, subjectId: string): { title: string; body: string; confirm: string } {
  const name = subjectName(subjectId).name;
  switch (action) {
    case 'release': return { title: `Release ${name}?`, body: `Pulse stops treating ${name} as failing now: Gyro stops warning people about it and stops refusing its second call in an answer. It reads recovering, and healthy after a clean day; new failures can mark it failing again. Pulse keeps measuring it and never switches it off.`, confirm: 'Release' };
    case 'mute': return { title: `Mute ${name}?`, body: `Pulse keeps measuring ${name} but stops telling the model about its failures, so Gyro no longer warns people before trying it.`, confirm: 'Mute' };
    case 'unmute': return { title: `Unmute ${name}?`, body: `Gyro warns people again before trying ${name} while it is failing.`, confirm: 'Unmute' };
  }
}

/** The status changes a problem offers from its current status (section 9.3). */
export function problemStatusActions(status: PulseProblemStatus): PulseProblemStatus[] {
  switch (status) {
    case 'new': return ['investigating', 'fixed-in', 'duplicate-of', 'wont-fix'];
    case 'investigating': return ['fix-proposed', 'fixed-in', 'duplicate-of', 'wont-fix'];
    case 'fix-proposed': return ['fixed-in', 'investigating', 'wont-fix'];
    case 'fixed-in': return ['verified', 'investigating'];
    case 'verified': return ['investigating'];
    case 'wont-fix': return ['investigating'];
    case 'duplicate-of': return ['investigating'];
  }
}

export const PROBLEM_ACTION_LABELS: Record<PulseProblemStatus, string> = {
  new: 'New', investigating: 'Investigating', 'fix-proposed': 'Fix proposed', 'fixed-in': 'Fixed in…', verified: 'Verified', 'wont-fix': "Won't fix", 'duplicate-of': 'Duplicate of…',
};

/** The text behind Copy "for the dev team": the weekly digest's paragraph, or the daily digest's suggested changes. */
export function forDevTeamText(digest: PulseDigest): string {
  if (digest.forDevTeam?.trim()) return digest.forDevTeam.trim();
  const lines: string[] = [`Pulse ${digest.kind} digest, ${digest.from} to ${digest.to}.`];
  for (const change of digest.suggestedChanges) {
    lines.push(`- ${change.title} (${change.kind}, ${change.confidence}): ${change.why}${change.problemId ? ` [${change.problemId}]` : ''}`);
  }
  for (const item of digest.notWorking.slice(0, 5)) {
    lines.push(`- Not working: "${item.intentKey.replace(/-/g, ' ')}" (${domainLabel(item.domain)}), ${item.asks} asks, ${trimNumber(item.couldNotPct)} % could not.`);
  }
  return lines.join('\n');
}

/** Three lines for the Overview when the service's summary is missing: the digest's first claims. */
export function digestSummary(digest: Pick<PulseDigest, 'notWorking' | 'health' | 'suggestedChanges' | 'notPossible'>): string {
  const lines: string[] = [];
  const health = digest.health.find((h) => h.to === 'failing' || h.to === 'quarantined');
  if (health) lines.push(`${subjectName(health.subjectId).name} has been ${health.to} ${sinceText(health.at)}${health.problemId ? ` (${health.problemId})` : ''}.`);
  const worst = digest.notWorking[0];
  if (worst) lines.push(`"${worst.intentKey.replace(/-/g, ' ')}" fails ${trimNumber(worst.couldNotPct)} % of the time over ${worst.asks} asks.`);
  const missing = digest.notPossible[0];
  if (missing) lines.push(`${missing.asks} asks for "${missing.intentKey.replace(/-/g, ' ')}"; ${reasonLabel(missing.reason)}.`);
  const change = digest.suggestedChanges[0];
  if (lines.length < 3 && change) lines.push(`Suggested: ${change.title}.`);
  return lines.slice(0, 3).join(' ');
}

/** Cache hit ratio (14.3): cacheRead / (cacheRead + input), as a percentage. */
export function cacheHitPct(row: Pick<PulseModelRow, 'cacheRead' | 'inputTokens'>): number | null {
  const total = row.cacheRead + row.inputTokens;
  return total > 0 ? Math.round((row.cacheRead / total) * 1000) / 10 : null;
}

/** The funnel's rates (14.3): confirmed of proposed, done of confirmed, expired and cancelled of proposed. */
export function funnelRates(row: PulseFunnelRow): { confirmedPct: number | null; donePct: number | null; expiredPct: number | null; cancelledPct: number | null } {
  const pct = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 1000) / 10 : null);
  return { confirmedPct: pct(row.confirmed, row.proposed), donePct: pct(row.done, row.confirmed), expiredPct: pct(row.expired, row.proposed), cancelledPct: pct(row.cancelled, row.proposed) };
}

/** `Record<key, row>` → rows with their key, for a table. */
export function keyed<T extends object>(map: Record<string, T> | null | undefined): Array<T & { key: string }> {
  return Object.entries(map ?? {}).map(([key, row]) => ({ ...row, key }));
}

/** The cost table's total (14.3): every domain, every outcome. */
export function costTotal(cost: PulseOperations['cost']): number {
  let total = 0;
  for (const outcomes of Object.values(cost)) for (const cell of Object.values(outcomes)) if (cell) total += cell.usd;
  return total;
}

/** The knowledge gaps as one list (mockup admin-13): no-source intents and World IQ company-looking intents, by asks. */
export function knowledgeGapRows(knowledge: PulseOperations['knowledge']): Array<{ intentKey: string; intent: string; kind: 'no-source' | 'world-company'; asks: number; sample: string | null }> {
  const rows = [
    ...(knowledge.noSourceIntents ?? []).map((row) => ({ intentKey: row.intentKey, intent: row.intent, kind: 'no-source' as const, asks: row.asks, sample: row.samples?.[0] ?? null })),
    ...(knowledge.likelyCompanyIntents ?? []).map((row) => ({ intentKey: row.intentKey, intent: row.intent, kind: 'world-company' as const, asks: row.asks, sample: row.samples[0] ?? null })),
  ];
  return rows.sort((a, b) => b.asks - a.asks);
}

// ------------------------------------------------------------------------------------------------ the probe and failures

/** The probe's answer: a 404 (`pulse_disabled`), 401 or 403 hides the rail group; any other failure keeps it (the pages say what failed). */
export function pulseVisible(probeError: unknown): boolean {
  if (probeError === null || probeError === undefined) return true;
  const status = (probeError as { status?: unknown }).status;
  return !(status === 404 || status === 401 || status === 403);
}

/** The rail group's state: `checking` until the probe answers (a deep link waits instead of flashing "not on"). */
export function pulseNav(consoleUser: boolean, probe: boolean | null): 'checking' | 'shown' | 'hidden' {
  if (!consoleUser) return 'hidden';
  if (probe === null) return 'checking';
  return probe ? 'shown' : 'hidden';
}

export function pulseReadFailure(error: unknown, what = 'The Pulse data could not be read.'): string {
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 404) return 'Pulse is switched off: the service answers 404 until the pulse flag is on under Feature flags.';
  if (status === 403) return 'Only IT admins can make this change.';
  if (status === 429) return 'One run an hour: the service refused this one. Try again later.';
  return errorText(error, what);
}
